import React from 'react';
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, ResponsiveContainer, Tooltip, Bar } from 'recharts';
import Icon from '../../../components/AppIcon';

const features = [
  { feature: 'Form Builder', adopted: 78, target: 85, icon: 'Wrench', color: '#107570' },
  { feature: 'Templates', adopted: 62, target: 70, icon: 'LayoutTemplate', color: '#0d9488' },
  { feature: 'Analytics', adopted: 45, target: 60, icon: 'BarChart3', color: '#14b8a6' },
  { feature: 'A/B Testing', adopted: 18, target: 35, icon: 'TestTube2', color: '#99f6e4' },
  { feature: 'Embed', adopted: 41, target: 55, icon: 'Code2', color: '#ccfbf1' },
  { feature: 'Payments', adopted: 22, target: 40, icon: 'CreditCard', color: '#f0fdfa' },
];

const radarData = features?.map(f => ({ subject: f?.feature, adopted: f?.adopted, target: f?.target }));

const CustomTooltip = ({ active, payload }) => {
  if (active && payload?.length) {
    return (
      <div className="bg-popover border border-border rounded-lg p-3 shadow-lg text-xs">
        <p className="font-semibold text-foreground mb-1">{payload?.[0]?.payload?.subject}</p>
        <p className="text-primary">Adopted: {payload?.[0]?.value}%</p>
        {payload?.[1] && <p className="text-muted-foreground">Target: {payload?.[1]?.value}%</p>}
      </div>
    );
  }
  return null;
};

const FeatureAdoption = () => {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Feature Adoption</h3>
          <p className="text-xs text-muted-foreground mt-0.5">% of active users using each feature</p>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-primary inline-block rounded" />Adopted</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-border inline-block rounded border-dashed border border-muted-foreground" />Target</span>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Bar list */}
        <div className="space-y-3">
          {features?.map(f => (
            <div key={f?.feature} className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${f?.color}20` }}>
                <Icon name={f?.icon} size={14} color={f?.color} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-foreground truncate">{f?.feature}</span>
                  <span className="text-xs font-semibold text-foreground ml-2 shrink-0">{f?.adopted}%</span>
                </div>
                <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden relative">
                  {/* Target marker */}
                  <div
                    className="absolute top-0 bottom-0 w-0.5 bg-muted-foreground/40 z-10"
                    style={{ left: `${f?.target}%` }}
                  />
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${f?.adopted}%`, backgroundColor: f?.color }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Radar chart */}
        <div className="flex items-center justify-center">
          <ResponsiveContainer width="100%" height={220}>
            <RadarChart data={radarData} margin={{ top: 10, right: 20, bottom: 10, left: 20 }}>
              <PolarGrid stroke="var(--color-border)" />
              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 9, fill: 'var(--color-muted-foreground)' }} />
              <Radar name="Adopted" dataKey="adopted" stroke="#107570" fill="#107570" fillOpacity={0.25} strokeWidth={2} />
              <Radar name="Target" dataKey="target" stroke="var(--color-muted-foreground)" fill="transparent" strokeWidth={1.5} strokeDasharray="4 2" />
              <Tooltip content={<CustomTooltip />} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default FeatureAdoption;
